import React from "react";
import { motion } from "framer-motion";
import { Store, Building2, BookOpen, ShoppingBag, ArrowRight } from "lucide-react";

const segments = [
  {
    icon: Store,
    title: "Kirana Store",
    desc: "Apni kirana dukaan ko online le jaiye — WhatsApp catalog, UPI payments, home delivery.",
    tone: "from-orange-500/30 via-orange-500/0 to-transparent",
    ring: "group-hover:ring-orange-400/40",
    iconColor: "text-orange-300",
    badge: "bg-orange-500/15 text-orange-200 border-orange-400/30",
  },
  {
    icon: Building2,
    title: "CSC Center",
    desc: "Professional website + citizen services portal + secure payment gateway in one click.",
    tone: "from-emerald-500/30 via-emerald-500/0 to-transparent",
    ring: "group-hover:ring-emerald-400/40",
    iconColor: "text-emerald-300",
    badge: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
  },
  {
    icon: BookOpen,
    title: "Tuition Center",
    desc: "Class schedules, fee collection, student registration — all on your own branded app.",
    tone: "from-blue-500/30 via-blue-500/0 to-transparent",
    ring: "group-hover:ring-blue-400/40",
    iconColor: "text-blue-300",
    badge: "bg-blue-500/15 text-blue-200 border-blue-400/30",
  },
  {
    icon: ShoppingBag,
    title: "Retail Shop",
    desc: "Boutique, electronics, mobile shop — premium storefront that brings customers to you.",
    tone: "from-pink-500/30 via-pink-500/0 to-transparent",
    ring: "group-hover:ring-pink-400/40",
    iconColor: "text-pink-300",
    badge: "bg-pink-500/15 text-pink-200 border-pink-400/30",
  },
];

export default function BuiltForBharat({ onCta }) {
  return (
    <section
      id="bharat"
      data-testid="bharat-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[70%] radial-glow-violet blur-3xl opacity-40" />
      {/* Tricolor accent bar */}
      <div className="absolute top-12 left-1/2 -translate-x-1/2 w-40 h-1 rounded-full overflow-hidden flex">
        <div className="flex-1 bg-[#FF9933]/80" />
        <div className="flex-1 bg-white/60" />
        <div className="flex-1 bg-[#138808]/80" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="uppercase text-xs tracking-[0.24em] text-violet-300 font-semibold">
            Built for Bharat 🇮🇳
          </p>
          <h2
            data-testid="bharat-heading"
            className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
          >
            Designed for every{" "}
            <span className="text-gradient-neon">local business across India</span>.
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto leading-relaxed">
            From the corner kirana to your town's CSC — we speak your language,
            understand your customers and ship what actually works in Bharat.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {segments.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 28, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`bharat-card-${i}`}
              className={`group relative rounded-2xl glass border border-white/5 p-6 transition-all ring-1 ring-white/5 ${s.ring} hover:-translate-y-1`}
            >
              <div className={`absolute -top-20 -right-10 w-48 h-48 bg-gradient-to-br ${s.tone} blur-3xl rounded-full opacity-80 group-hover:opacity-100 transition-opacity`} />
              <div className="relative">
                <div className="w-14 h-14 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center animate-floaty-slow">
                  <s.icon className={`w-6 h-6 ${s.iconColor}`} strokeWidth={1.6} />
                </div>
                <span className={`mt-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border ${s.badge}`}>
                  Bharat ready
                </span>
                <h3 className="mt-3 font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-1 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <button
            onClick={onCta}
            data-testid="bharat-cta"
            className="group px-7 py-3.5 rounded-full btn-primary-grad text-white font-semibold text-base transition-all hover:-translate-y-0.5 shadow-2xl shadow-violet-500/40 flex items-center gap-2"
          >
            Start Your Online Journey
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}
